import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LearnService } from "@/services/learn/LearnService";
import { LearnArticleFilters } from "@/types/learn";

const STALE = 5 * 60 * 1000;

export const learnQueryKeys = {
  all: ["learn"] as const,
  articles: (filters?: LearnArticleFilters) => ["learn", "articles", filters ?? null] as const,
  article: (slug: string) => ["learn", "article", slug] as const,
  progress: () => ["learn", "progress"] as const,
};

export const useLearnArticles = (filters?: LearnArticleFilters) => {
  return useQuery({
    queryKey: learnQueryKeys.articles(filters),
    queryFn: () => LearnService.list(filters),
    staleTime: STALE,
  });
};

export const useStartHereArticles = () => {
  return useLearnArticles({ startHere: true });
};

export const useLearnArticle = (slug: string | undefined) => {
  return useQuery({
    queryKey: slug ? learnQueryKeys.article(slug) : ["learn", "article", "none"],
    queryFn: () => (slug ? LearnService.getBySlug(slug) : Promise.resolve(null)),
    enabled: !!slug,
    staleTime: STALE,
  });
};

export const useLearnProgress = () => {
  return useQuery({
    queryKey: learnQueryKeys.progress(),
    queryFn: () => LearnService.getProgress(),
    staleTime: 60 * 1000,
  });
};

export const useMarkArticleComplete = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (articleId: string) => LearnService.markComplete(articleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learnQueryKeys.all });
    },
  });
};

export const useMarkArticleIncomplete = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (articleId: string) => LearnService.markIncomplete(articleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learnQueryKeys.all });
    },
  });
};
